import request from '@/utils/request'

/**
 * 发起群聊。
 *
 * @param {Object} data
 * @param {Array<String>} data.memberUserIds 受邀成员（不含自己）。后端要求至少 2 人、最多 199 人
 * @param {String} [data.name]   群名，留空时后端按「我、A、B」自动生成，最长 20 字
 * @param {String} [data.avatar] 群头像，留空时前端用成员头像拼图展示
 */
export function createChatGroup(data) {
  return request({
    url: '/v1/group',
    method: 'post',
    data
  })
}

/**
 * 邀请成员入群。开启入群审核且自己不是管理员时，后端会转成入群申请。
 */
export function addChatGroupMembers(groupId, memberUserIds) {
  return request({
    url: `/v1/group/${groupId}/members`,
    method: 'post',
    data: { memberUserIds }
  })
}

export function listJoinedChatGroups(params = {}) {
  return request({
    url: '/v1/group/joined',
    method: 'get',
    params: {
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 20
    }
  })
}

export function getChatGroup(groupId) {
  return request({
    url: `/v1/group/${groupId}`,
    method: 'get'
  })
}

export function getGroupChatHistory(params) {
  return request({
    url: `/v1/group/${params.groupId}/history`,
    method: 'get',
    params: {
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 20
    }
  })
}

export function hideGroupChatMessage(messageId) {
  return request({
    url: `/v1/group/message/${messageId}`,
    method: 'delete'
  })
}

export function previewChatGroupInvite(token) {
  return request({
    url: `/v1/group/invite/${token}`,
    method: 'get'
  })
}

export function joinChatGroupByToken(token) {
  return request({
    url: '/v1/group/join',
    method: 'post',
    data: { token }
  })
}

export function reportChatGroup(groupId, data) {
  return request({
    url: `/v1/group/${groupId}/report`,
    method: 'post',
    data
  })
}

export function quitChatGroup(groupId) {
  return request({
    url: `/v1/group/${groupId}/quit`,
    method: 'post'
  })
}

export function dismissChatGroup(groupId) {
  return request({
    url: `/v1/group/${groupId}/dismiss`,
    method: 'post'
  })
}

export function updateChatGroup(groupId, data) {
  return request({
    url: `/v1/group/${groupId}`,
    method: 'put',
    data
  })
}

export function getChatGroupMembers(groupId, params = {}) {
  return request({
    url: `/v1/group/${groupId}/members`,
    method: 'get',
    params: {
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50
    }
  })
}

/**
 * 把成员移出群聊（群主或管理员；管理员不能移出其他管理员）
 */
export function kickChatGroupMember(groupId, userId) {
  return request({
    url: `/v1/group/${groupId}/members/${userId}`,
    method: 'delete'
  })
}

/**
 * 设置 / 取消管理员（仅群主）
 *
 * @param {Number} role 0-普通成员 2-管理员（1 是群主，只能通过转让产生）
 */
export function updateChatGroupMemberRole(groupId, userId, role) {
  return request({
    url: `/v1/group/${groupId}/members/${userId}/role`,
    method: 'put',
    data: { role }
  })
}

/**
 * 修改我在本群的昵称。传空字符串即清除，最长 16 字
 */
export function updateChatGroupNickname(groupId, nickname) {
  return request({
    url: `/v1/group/${groupId}/nickname`,
    method: 'put',
    data: { nickname }
  })
}

/**
 * 获取群邀请令牌。返回 { token, groupId, groupName, groupAvatar, memberAvatars, expireAt }
 */
export function getChatGroupQrcode(groupId) {
  return request({
    url: `/v1/group/${groupId}/qrcode`,
    method: 'get'
  })
}

/**
 * 刷新群邀请令牌，旧链接立即失效（群主或管理员）
 */
export function refreshChatGroupQrcode(groupId) {
  return request({
    url: `/v1/group/${groupId}/qrcode/refresh`,
    method: 'post'
  })
}

/**
 * 群内查找聊天记录
 */
export function searchChatGroupMessages(groupId, params = {}) {
  return request({
    url: `/v1/group/${groupId}/search`,
    method: 'get',
    params: {
      keyword: params.keyword || '',
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 20
    }
  })
}

export function transferChatGroupOwner(groupId, userId) {
  return request({
    url: `/v1/group/${groupId}/transfer`,
    method: 'post',
    data: { userId }
  })
}

export function muteChatGroupMember(groupId, userId, muted) {
  return request({
    url: `/v1/group/${groupId}/members/${userId}/mute`,
    method: 'put',
    data: { muted }
  })
}

export function getChatGroupJoinRequests(groupId, params = {}) {
  return request({
    url: `/v1/group/${groupId}/join-requests`,
    method: 'get',
    params: {
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 20
    }
  })
}

export function handleChatGroupJoinRequest(groupId, requestId, status) {
  return request({
    url: `/v1/group/${groupId}/join-requests/${encodeURIComponent(String(requestId))}`,
    method: 'put',
    data: { status }
  })
}

/**
 * HTTP 通道发群消息（分享视频到群聊用）。
 *
 * 私信页自己维护 WebSocket 连接；播放器里分享一条视频只发一次，不值得为它单独建连接，
 * 和个人主页「发私信」一样走 HTTP（服务端限流 60 条/分钟）。
 *
 * @param {Object} data { groupId, content, imageUrls, messageType }
 */
export function sendGroupChatMessage(data) {
  return request({
    url: '/v1/group/send',
    method: 'post',
    data
  })
}
